/**
 * Shared booking normalisation — used by Layout (prefetch) and ShowsPage.
 *
 * Extracted so the Layout can pre-populate the localStorage cache before
 * the user even navigates to Shows, eliminating any loading flash.
 */

export interface NormalizedShow {
  id: string
  date?: string
  perf_date?: string
  end_date?: string
  title?: string
  event_title?: string
  venue?: string
  city?: string
  state?: string
  ticket_count?: number
  tix_sold?: number
  status?: string
  sellable_cap?: number
  tix_sold_percent?: number
  artist?: string
  source?: string
  guarantee?: string | number
  bonus_tiers?: Array<{ tickets_threshold: number; bonus_amount: number }>
  support_acts?: Array<{ name: string; time_start: string; time_end: string }>
  ticket_tracking?: boolean
  on_sale?: boolean
  last_ticket_count_date?: string
  /** 'synced' | 'pending' | 'out_of_sync' - from backend when available */
  syncStatus?: 'synced' | 'pending' | 'out_of_sync'
  raw?: any
}

// ── Date normaliser ────────────────────────────────────────────────
// Accepts common CSV/Excel-ish date strings and returns canonical YYYY-MM-DD.
export function normalizeLooseDate(raw: string): string | undefined {
  const value = raw.trim()
  if (!value) return undefined

  const token =
    value.match(/^(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
    value.match(/(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
    value

  const patterns: Array<{ re: RegExp; kind: 'mdy' | 'dmy' | 'ymd' }> = [
    { re: /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/, kind: 'mdy' },
    { re: /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/, kind: 'mdy' },
    { re: /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/, kind: 'dmy' },
    { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, kind: 'ymd' },
    { re: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, kind: 'ymd' },
    { re: /^(\d{4})\.(\d{1,2})\.(\d{1,2})$/, kind: 'ymd' },
  ]

  for (const p of patterns) {
    const m = token.match(p.re)
    if (!m) continue

    let year = '', month = '', day = ''
    if (p.kind === 'ymd') { year = m[1]; month = m[2]; day = m[3] }
    else if (p.kind === 'mdy') { month = m[1]; day = m[2]; year = m[3] }
    else { day = m[1]; month = m[2]; year = m[3] }

    const yearNumRaw = Number(year)
    const yearNum = year.length === 2 ? 2000 + yearNumRaw : yearNumRaw
    const mmNum = Number(month)
    const ddNum = Number(day)

    const d = new Date(yearNum, mmNum - 1, ddNum, 12, 0, 0, 0)
    if (d.getFullYear() === yearNum && d.getMonth() === mmNum - 1 && d.getDate() === ddNum) {
      return `${String(yearNum)}-${String(mmNum).padStart(2, '0')}-${String(ddNum).padStart(2, '0')}`
    }
  }

  const cleaned = token.replace(/^(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\s*,?\s*/i, '')
  const parsed = new Date(cleaned)
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
  }

  return undefined
}

// ── Booking normaliser ─────────────────────────────────────────────
/**
 * Normalise raw API bookings payload into a flat Show array.
 *
 * @param payload - raw Axios response or unwrapped data from `user/getBookings`
 * @param trackingOverrides - optional map of showId → boolean from localStorage
 */
export function normalizeBookingsData(
  payload: any,
  trackingOverrides: Record<string, boolean> = {},
): NormalizedShow[] {
  // Backend may return { data: [...] }, { data: { data: [...] } }, { bookings: [...] }, or a raw array
  const list =
    payload?.data?.data ??
    payload?.data ??
    payload?.bookings ??
    (Array.isArray(payload) ? payload : null) ??
    []
  const array: any[] = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : []

  return array.map((item: any) => {
    let city = item.city
    let state = item.state ?? item.state_province
    if ((!city || !state) && item.city_state) {
      const parts = String(item.city_state).split(',').map((s: string) => s.trim())
      if (!city && parts[0]) city = parts[0]
      if (!state && parts[1]) state = parts[1]
    }

    const rawPerf = (item.perf_date ?? item.date ?? '').toString?.() ?? String(item.perf_date ?? item.date ?? '')
    const normalizedPerf = rawPerf ? normalizeLooseDate(rawPerf) : undefined
    const eventTitle = item.event_title || item.title || ''
    const venueVal = item.venue || ''
    const showId = item._id || item.id

    // Resolve ticket_tracking
    let tracking: boolean
    if (showId && trackingOverrides[showId] !== undefined) {
      tracking = trackingOverrides[showId]
    } else if (item.ticket_tracking === false || item.ticket_tracking === 'false' || item.ticket_tracking === 0) {
      tracking = false
    } else if (item.ticket_tracking === true || item.ticket_tracking === 'true' || item.ticket_tracking === 1) {
      tracking = true
    } else {
      const lowerTitle = String(eventTitle).toLowerCase()
      const lowerVenue = String(venueVal).toLowerCase()
      tracking = !(lowerTitle.includes('festival') || lowerVenue.includes('festival'))
    }

    // Resolve on_sale (handle boolean, string "true"/"false", and null)
    const rawOnSale = item.on_sale
    const onSale = rawOnSale === true || rawOnSale === 'true' || rawOnSale === 1

    return {
      id: showId,
      date: normalizedPerf || item.date || item.perf_date,
      perf_date: normalizedPerf || item.perf_date || item.date,
      end_date: item.end_date || undefined,
      title: eventTitle || venueVal,
      event_title: item.event_title || item.title,
      venue: venueVal,
      city,
      state,
      artist: item.artist || item.artist_name,
      tix_sold: item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0,
      ticket_count: item.ticket_count ?? item.tickets_sold ?? item.tix_sold ?? item.current_ticket_count ?? 0,
      status: item.status,
      sellable_cap: item.sellable_cap ?? item.capacity ?? item.cap ?? item.venue_capacity,
      tix_sold_percent: item.tix_sold_percent ?? item.percentage_sold,
      guarantee: item.guarantee,
      bonus_tiers: Array.isArray(item.bonus_tiers) ? item.bonus_tiers : undefined,
      support_acts: Array.isArray(item.support_acts) ? item.support_acts : undefined,
      source: item.source,
      ticket_tracking: tracking,
      on_sale: onSale,
      last_ticket_count_date: item.last_ticket_count_date || undefined,
      visibility: item.visibility || 'team',
      syncStatus: item.syncStatus || item.sync_status || undefined,
      raw: item,
    }
  })
}
